/* oxlint-disable react/refs -- imperative refs are required for DOM, focus, drag, and media behavior */
import { getStringWidth } from '@visx/text'
import { ReactElement, useEffect, useRef, useState } from 'react'
import { getName } from '../LazyLocationCell'
import { Tooltip } from '../Tooltip'
import { asyncParallel } from 'lib/asyncParallel'
import { bg } from 'lib/background'

/**
 * Component that shows a list of lazy loaded location names.
 *
 * @param locationIds string[] - array of location ids
 * @returns Component that shows a list of location names if there is enough space to show them. Otherwise it shows a tooltip for hidden location names.
 */
export function LazyLocationsCell({ locationIds }: { locationIds: string[] }): ReactElement {
  const observerRef = useRef<ResizeObserver>(undefined)
  const ref = useRef<HTMLDivElement>(null)
  const [locationNames, setLocationNames] = useState<string[]>([])
  const [visibleLocations, setVisibleLocations] = useState<string[]>([])
  const [hiddenLocations, setHiddenLocations] = useState<string[]>([])

  const getEstimatedWidth = (name: string) => {
    return (
      (getStringWidth(name, {
        fontSize: `14px`,
        lineHeight: `19.6px`,
        fontWeight: 400,
        fontFamily: `Inter, sans-serif`,
      }) ?? 0) + 20
    )
  }

  const updateVisibleText = () => {
    // 65 is the width between the last location name (or tooltip number) and the right edge of the cell
    // this value avoids the location cell to be over the right edge of the cell
    let availableWidth = (ref.current?.offsetWidth ?? 0) - 65
    let visible: string[] = []
    let usedWidth = 0
    locationNames.forEach((name) => {
      let nameWidth = getEstimatedWidth(name)
      if (usedWidth + nameWidth <= availableWidth) {
        visible.push(name)
        usedWidth += nameWidth
      }
    })

    setVisibleLocations(visible)
    setHiddenLocations(locationNames.filter((name) => !visible.includes(name)))
  }

  useEffect(() => updateVisibleText(), [locationNames])

  useEffect(() => {
    const names: string[] = []
    if (!locationIds) return
    bg(async () => {
      await asyncParallel(locationIds, async (id) =>
        getName(id).then((response) => {
          names.push(response as string)
        }),
      )
      setLocationNames(names)
    })
  }, [locationIds])

  useEffect(() => {
    const resizeObserver = new ResizeObserver((entries) => {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      for (let entry of entries) {
        updateVisibleText()
      }
    })
    if (ref.current) {
      observerRef.current?.disconnect()
      resizeObserver.observe(ref.current)
      observerRef.current = resizeObserver
    }

    return () => {
      observerRef.current?.disconnect()
    }
  }, [ref.current, locationNames])

  useEffect(() => {
    observerRef.current?.disconnect()

    return () => {
      setLocationNames([])
      setVisibleLocations([])
      setHiddenLocations([])
    }
  }, [locationIds])

  return (
    <div ref={ref} className="flex h-full w-full flex-nowrap items-center gap-1 px-2">
      {visibleLocations?.map((name: string) => (
        <span key={name} className="tag m-1 px-2">
          {name}
        </span>
      ))}
      <Tooltip
        message={
          hiddenLocations.length ? (
            <div>
              {hiddenLocations?.map((name) => (
                <div className="py-1" key={name}>
                  {name}
                </div>
              ))}
            </div>
          ) : null
        }
      >
        {hiddenLocations?.length ? (
          <div className="tag cursor-default px-1.5">{`+${hiddenLocations?.length}`}</div>
        ) : null}
      </Tooltip>
    </div>
  )
}
